import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { handleGenericError } from '../handle-generic-error';
import authSlice from '../auth/auth-slice';

export const fetchDocumentData = createAsyncThunk(
  'documents/fetch-document-data',
  async ({ itemAssignmentCode, navigate }, thunkAPI) => {
    const zensignApiUrl = window.config.zensignApiUrl;
    await axios
      .get(`${zensignApiUrl}/external-assignments/document/document-assignments/${itemAssignmentCode}`)
      .then((response) => {
        thunkAPI.dispatch(documentsSlice.actions.fetchDocumentDataSuccess(response.data));
      })
      .catch((err) => {
        if (err.response.status === 403) {
          thunkAPI.dispatch(authSlice.actions.sessionConflict(itemAssignmentCode));
          throw thunkAPI.rejectWithValue({ ...err.response.data });
        }
        handleGenericError(thunkAPI.dispatch, err, navigate);
        throw thunkAPI.rejectWithValue({ ...err.response.data });
      });
  }
);

export const acceptDocumentTos = createAsyncThunk(
  'documents/update-tos',
  async ({ itemAssignmentCode, navigate }, thunkAPI) => {
    const zensignApiUrl = window.config.zensignApiUrl;
    await axios
      .put(`${zensignApiUrl}/external-assignments/document/document-assignments/${itemAssignmentCode}/accept-tos`)
      .then((response) => {
        thunkAPI.dispatch(documentsSlice.actions.tosAccepted(response.data));
      })
      .catch((err) => {
        handleGenericError(thunkAPI.dispatch, err, navigate);
        throw thunkAPI.rejectWithValue({ ...err.response.data });
      });
  }
);

export const documentApprove = createAsyncThunk(
  'documents/document-approve',
  async ({ itemAssignmentCode, signatoryEmail, role, securityCode, comment, isApproved, navigate }, thunkAPI) => {
    const zensignApiUrl = window.config.zensignApiUrl;
    return await axios
      .post(`${zensignApiUrl}/external-assignee/verify/sign-document`, {
        email: signatoryEmail,
        role: role,
        code: itemAssignmentCode,
        securityCode: securityCode,
        comment: comment,
        isApproved: isApproved,
      })
      .then((response) => {
        isApproved
          ? thunkAPI.dispatch(documentsSlice.actions.documentApproved(response.data))
          : thunkAPI.dispatch(documentsSlice.actions.documentRejected(response.data));
        return response.data;
      })
      .catch((err) => {
        if (err.response.status === 422 && err.response.data.errors) {
          const errorMessageKey = err.response.data.errors[0].message;

          throw thunkAPI.rejectWithValue({
            errorCode: errorMessageKey,
          });
        } else {
          handleGenericError(thunkAPI.dispatch, err, navigate);
          throw thunkAPI.rejectWithValue({ ...err.response.data });
        }
      });
  }
);

function getTosAcceptedState(action) {
  return action.payload.data.tosAccepted ? 'Accepted' : 'NotAccepted';
}

const documentsSlice = createSlice({
  name: 'documents',
  initialState: {
    document: {
      name: '',
      revision: {
        watermarkOptions: {},
        fileExtension: '',
        url: '',
      },
      signed: false,
      approvalError: '',
      tosStatus: '',
      workflow: {},
      externalAssigneeId: '',
      signedType: '',
    },
  },
  reducers: {
    fetchDocumentDataSuccess: (state, action) => {
      return {
        ...state,
        document: {
          ...state.document,
          ...action.payload.data.document,
          externalAssigneeId: action.payload.data.externalAssigneeId,
          tosStatus: getTosAcceptedState(action),
          workflow: {
            ...state.document.workflow,
            ...action.payload.data.workflow,
          },
        },
      };
    },
    tosAccepted: (state) => {
      return {
        ...state,
        document: {
          ...state.document,
          tosStatus: 'Accepted',
        },
      };
    },
    documentApproved: (state) => {
      return {
        ...state,
        document: {
          ...state.document,
          signed: true,
          signedType: 'approved',
          approvalError: '',
        },
      };
    },
    documentRejected: (state) => {
      return {
        ...state,
        document: {
          ...state.document,
          signed: true,
          signedType: 'rejected',
          approvalError: '',
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(documentApprove.rejected, (state, action) => {
        state.document.approvalError = action.payload.errorCode;
      })
      .addCase(fetchDocumentData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchDocumentData.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchDocumentData.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addDefaultCase(() => {});
  },
});

export default documentsSlice;
export const { reducer } = documentsSlice;
export const { fetchDocumentDataSuccess, documentApproved } = documentsSlice.actions;
